import { useEffect, useState } from 'react'
import useSWR from 'swr'
import { api, fetcher } from '../api/client'

/*
 * Discovery flow:
 *  1. user fills form (company/website/linkedin/industry/area)
 *  2. POST /api/discover → jobId
 *  3. poll /status until candidates_found
 *  4. show candidates, user selects subset → POST /enrich-selected
 *  5. enrichment runs in background, prospects appear under /prospects
 */
export default function DiscoverPage() {
    const [form, setForm] = useState({
        companyName: '', website: '', linkedInUrl: '', industry: '', area: ''
    })
    const [jobId, setJobId] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    const [err, setErr] = useState<string | null>(null)
    const [selected, setSelected] = useState<Set<string>>(new Set())
    const [enrichmentMsg, setEnrichmentMsg] = useState<string | null>(null)

    /* poll status and candidates while job runs */
    const { data: status } = useSWR<any>(
        jobId ? `/api/discover/${jobId}/status` : null,
        fetcher,
        { refreshInterval: 2000 }
    )
    const { data: candidates } = useSWR<any[]>(
        jobId && status?.status !== 'Pending' ? `/api/discover/${jobId}/candidates` : null,
        fetcher,
        { refreshInterval: status?.status === 'Searching' ? 3000 : 0 }
    )

    async function startDiscovery(e: React.FormEvent) {
        e.preventDefault()
        setBusy(true); setErr(null); setSelected(new Set())
        try {
            const resp: any = await api('/api/discover', { method: 'POST', body: JSON.stringify(form) })
            setJobId(resp.jobId)
        } catch (e: any) {
            setErr(e.message)
        } finally {
            setBusy(false)
        }
    }

    async function enrichSelected() {
        if (selected.size === 0 || !jobId) return
        setBusy(true); setErr(null)
        try {
            await api(`/api/discover/${jobId}/enrich-selected`, {
                method: 'POST',
                body: JSON.stringify({ candidateIds: [...selected] })
            })
            setEnrichmentMsg(`${selected.size} enrichment jobs queued — prospects will appear in the Prospects list as they finish.`)
            setSelected(new Set())
        } catch (e: any) {
            setErr(e.message)
        } finally {
            setBusy(false)
        }
    }

    function toggle(id: string) {
        const next = new Set(selected)
        next.has(id) ? next.delete(id) : next.add(id)
        setSelected(next)
    }

    return (
        <div>
            <h1>Discover Prospects</h1>
            <p className="muted">Enter what you know about the company. We'll find candidates you can vet for full enrichment.</p>

            <form onSubmit={startDiscovery} className="card">
                <div className="split">
                    <div className="form-row">
                        <label>Company Name</label>
                        <input value={form.companyName} onChange={e => setForm({ ...form, companyName: e.target.value })} placeholder="Acme Corp" />
                    </div>
                    <div className="form-row">
                        <label>Website</label>
                        <input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://acme.com" />
                    </div>
                    <div className="form-row">
                        <label>LinkedIn URL</label>
                        <input value={form.linkedInUrl} onChange={e => setForm({ ...form, linkedInUrl: e.target.value })} placeholder="https://linkedin.com/company/acme" />
                    </div>
                    <div className="form-row">
                        <label>Industry</label>
                        <input value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} placeholder="SaaS, Fintech, etc." />
                    </div>
                    <div className="form-row">
                        <label>Area / Location</label>
                        <input value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} placeholder="San Francisco, Remote, etc." />
                    </div>
                </div>
                {err && <div className="error">{err}</div>}
                <button type="submit" disabled={busy} data-testid="start-discovery">
                    {busy ? 'Starting...' : 'Start Discovery'}
                </button>
            </form>

            {jobId && (
                <div className="card">
                    <h2>Discovery Status</h2>
                    <p>Job: <span className="kbd">{jobId.slice(0, 8)}</span> — Status: <strong>{status?.status ?? '...'}</strong>
                        {status?.candidatesFound > 0 && <> · <strong>{status.candidatesFound}</strong> candidates found</>}
                    </p>
                    {status?.errorMessage && <div className="error">{status.errorMessage}</div>}
                </div>
            )}

            {candidates && candidates.length > 0 && (
                <div className="card">
                    <div className="row">
                        <h2 style={{ margin: 0 }}>Candidates ({candidates.length})</h2>
                        <div className="grow"></div>
                        <span className="muted">{selected.size} selected</span>
                        <button onClick={enrichSelected} disabled={selected.size === 0 || busy} data-testid="enrich-selected">
                            Enrich Selected →
                        </button>
                    </div>

                    {enrichmentMsg && <div className="success" style={{ marginTop: 12 }}>{enrichmentMsg}</div>}

                    <table>
                        <thead><tr><th></th><th>Company</th><th>Industry</th><th>Website</th></tr></thead>
                        <tbody>
                            {candidates.map(c => (
                                <tr key={c.id}>
                                    <td><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} data-testid={`cand-${c.id}`} /></td>
                                    <td>{c.companyName}</td>
                                    <td className="muted">{c.industry ?? '—'}</td>
                                    <td>{c.website ? <a href={c.website} target="_blank" rel="noreferrer">{shortUrl(c.website)}</a> : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <DiscoveryHistory onSelect={(id) => { setJobId(id); setSelected(new Set()); setEnrichmentMsg(null) }} activeJobId={jobId} />
        </div>
    )
}

function shortUrl(u: string) { try { return new URL(u).host } catch { return u } }

/* Past discovery jobs — newest first. Click to load candidates back into the page. */
function DiscoveryHistory({ onSelect, activeJobId }: { onSelect: (id: string) => void, activeJobId: string | null }) {
    const { data, mutate } = useSWR<any[]>('/api/discover?limit=50', fetcher)

    async function deleteJob(id: string) {
        if (!confirm('Delete this discovery job and all its candidates?')) return
        await api(`/api/discover/${id}`, { method: 'DELETE' })
        mutate()
    }

    return (
        <div className="card">
            <h2>Past Discoveries</h2>
            {!data ? <p className="muted">Loading...</p> :
                data.length === 0 ? <p className="muted">No discovery jobs yet.</p> :
                    <table>
                        <thead><tr><th>Started</th><th>Company / Industry</th><th>Status</th><th>Candidates</th><th></th></tr></thead>
                        <tbody>
                            {data.map(j => (
                                <tr key={j.id} style={{ background: activeJobId === j.id ? '#eff6ff' : undefined }}>
                                    <td className="muted">{new Date(j.createdAt).toLocaleString()}</td>
                                    <td>
                                        {j.companyName ?? '—'}
                                        {j.industry && <span className="muted"> · {j.industry}</span>}
                                    </td>
                                    <td>{j.status}</td>
                                    <td>{j.candidatesFound}</td>
                                    <td className="row" style={{ gap: 8 }}>
                                        {j.candidatesFound > 0 && (
                                            <button className="secondary" onClick={() => onSelect(j.id)}>Load</button>
                                        )}
                                        <button className="danger" onClick={() => deleteJob(j.id)}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
            }
        </div>
    )
}
